// @ts-nocheck
import { stableHash } from '@srcDS/utils/stableHash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import app from '@frontastic/catwalk/src/js/app/app';
import clsx from 'clsx';
import SwiperCore from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { convertTextToId } from '@srcDS/utils/convertTextToId';
import Button from '../../../atoms/button';
import ArrowLeft from '../../../atoms/icons/ArrowLeft';
import ArrowRight from '../../../atoms/icons/ArrowRight';
import TypeformWidget from '../../../organisms/typeform/TypeformWidget';
import ArticleItem from '../articleItem';
import ExternalScriptBlock from '../externalScript';
import { formatUrl } from '../utils';
import VideoBlockWrapper from '../videoBlockWrapper';
import styles from './ArticleSection.module.scss';

export interface ButtonType {
  viewType: string;
  url: string;
  title: string;
  [key: string]: any;
}

export interface BaseArticleItem {
  type?: string;
  buttons?: ButtonType[];
  form?: string | null;
  mediaTitle?: string;
  mediaTitlePosition?: 'left' | 'right' | string;
  linkTitle?: string;
  urlPosition?: string;
  [key: string]: any;
}

export type ArticleItemType = BaseArticleItem;

export interface ArticleSectionProps {
  sliderId?: string;
  articleItems: ArticleItemType[];
  rowAlign?: 'start' | 'end' | 'center';
  verticalAlign?: 'start' | 'end' | 'stretch';
  gridType?: 'asSlider' | 'asDefault' | 'asGridOnMobile';
  gap?: number;
  paddingBetweenSection?: number;
  maxWidth?: string | number;
  applyHeightTo?: string;
  blockHeight?: string | number;
  isSSR: boolean;
}

const ArticleSection: React.FC<ArticleSectionProps> = ({
  sliderId,
  articleItems,
  rowAlign,
  verticalAlign,
  gridType,
  gap,
  paddingBetweenSection,
  maxWidth,
  applyHeightTo,
  blockHeight,
  isSSR,
}) => {
  const scriptBlocks = articleItems?.filter((e) => e.type === 'scripts');

  const articleDefaultProps = { gridType, itemsCount: articleItems?.length };
  const swiperRef = useRef<{ swiper?: SwiperCore } | null>(null);

  useEffect(() => {
    if (gridType === 'asSlider' && swiperRef.current && swiperRef.current.swiper) {
      setTimeout(() => {
        swiperRef.current.swiper.update();
      }, 100);
    }
  }, []);

  if (scriptBlocks.length > 0) {
    return scriptBlocks.map((block, index) => {
      return (
        <div
          key={`script_${index}`}
          data-article-section="article_section_script"
          className={clsx(
            styles.container,
            styles.widgetBlock,
            styles[`align_${rowAlign}`],
            styles[`vertical_align_${verticalAlign}`],
            {
              [`${styles.limitedWidth}`]: !!maxWidth,
            },
          )}
          style={{
            maxWidth,
            '--article-item-margin': `${paddingBetweenSection}px` || 0,
          }}
        >
          <ExternalScriptBlock isSSR={isSSR} {...block} />
        </div>
      );
    });
  }

  const renderArticleItem = (item: ArticleItemType, key: string | number) => {
    if (item.type === 'videoBlock') {
      const randomKey = (Math.random() + 1).toString(36).substring(7);
      return <VideoBlockWrapper key={`videoBlock_${key}`} videoId={`videoBlock_${key}_${randomKey}`} {...item} />;
    }

    return (
      <ArticleItem
        key={`${convertTextToId(`article_${key}`)}_${key}`}
        {...item}
        {...articleDefaultProps}
        applyHeightTo={applyHeightTo}
        blockHeight={blockHeight}
      />
    );
  };

  if (gridType === 'asSlider') {
    const SliderSettings = {
      modules: [Pagination, Navigation],
      spaceBetween: 6,
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      watchOverflow: false,
      loop: true,
      preloadImages: true,
      navigation: {
        nextEl: `.${sliderId}-button-next`,
        prevEl: `.${sliderId}-button-prev`,
      },
      pagination: {
        clickable: true,
        el: `.${sliderId}-swiper-dots`,
        type: 'bullets',
        bulletActiveClass: styles.activeDot,
      },
    };
    return (
      <div style={{ maxWidth }} className={clsx(styles.swiperContainer, { [styles.hasMaxWidth]: maxWidth })}>
        <Swiper ref={swiperRef} {...SliderSettings}>
          {articleItems?.map((item, index) => (
            <SwiperSlide key={`article_slider_${index}`} className={styles.slide}>
              {renderArticleItem(item, index)}
            </SwiperSlide>
          ))}
        </Swiper>
        <div data-carousel-arrow="arrow_left" className={`${styles.buttonPrev} ${sliderId}-button-prev`}>
          <ArrowLeft />
        </div>
        <div data-carousel-arrow="arrow_right" className={`${styles.buttonNext} ${sliderId}-button-next`}>
          <ArrowRight />
        </div>

        <div className={`${styles.sliderDots} ${sliderId}-swiper-dots`} />
      </div>
    );
  }

  const hasVerticalTitle = articleItems.find(
    (item) => item.mediaTitle && (item.mediaTitlePosition === 'left' || item.mediaTitlePosition === 'right'),
  );

  return (
    <div
      data-article-section="article_section"
      className={clsx(
        styles.container,
        styles[`align_${rowAlign}`],
        styles[`vertical_align_${verticalAlign}`],
        styles[`count_${articleItems?.length}`],
        {
          [`${styles.mobileGrid}`]: gridType === 'asGridOnMobile' && !hasVerticalTitle,
          [`${styles.evenItems}`]: articleItems?.length % 2 === 0,
          [`${styles.limitedWidth}`]: !!maxWidth,
          [`${styles.buttonsWrapper}`]: articleItems.find((item) => 'buttons' in item),
        },
      )}
      style={{
        maxWidth,
        '--column-count': articleItems.length,
        '--article-item-gap': `${gap}px`,
        '--article-item-margin': `${paddingBetweenSection}px` || 0,
      }}
    >
      {articleItems?.map((item, index) => {
        if ('buttons' in item) {
          return (
            <div key={stableHash(JSON.stringify(item)+index)} className={styles.buttonGroup}>
              {item.buttons.map((button, key) => (
                <Button
                  key={`buttons_${key}_${index}`}
                  as={button.viewType === 'link' ? 'text' : 'button'}
                  variant={button.viewType}
                  shape="square"
                  size="medium"
                  onClick={() => app.getRouter().history.push(formatUrl(button.url))}
                  className={clsx(styles.articleButtons, styles[`type_${button.viewType}`])}
                >
                  {button.title}
                </Button>
              ))}
            </div>
          );
        }

        if ('form' in item && item?.form !== null) {
          let url = item?.form;
          let parts = url.split('/');
          let formId = parts[parts.length - 1];

          return <TypeformWidget formId={formId} />;
        }
        return renderArticleItem(item, `article_${index}`);
      })}
    </div>
  );
};

ArticleSection.propTypes = {
  rowAlign: PropTypes.oneOf(['start', 'end', 'center']),
  verticalAlign: PropTypes.oneOf(['start', 'end', 'stretch']),
  articleItems: PropTypes.array,
  gridType: PropTypes.oneOf(['asSlider', 'asDefault', 'asGridOnMobile']),
  gap: PropTypes.number,
  paddingBetweenSection: PropTypes.number,
};

ArticleSection.defaultProps = {
  rowAlign: 'start',
  gridType: 'asDefault',
  gap: 20,
};

export default ArticleSection;
